import { useCallback, useEffect, useMemo, useState } from "react";
import { listRows, updateRow } from "@/lib/adminApi";
import {
  APPLICATION_STAGE,
  metaFor,
  optionsFrom,
  type FarmerApplication,
} from "@/lib/adminModules";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  btnAccent,
  btnGhost,
  btnPrimary,
  fieldInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

const OFFICERS = ["Unassigned", "Vijay Kumar", "Priya Sharma"];
const IN_REVIEW = ["screening", "field_verification", "documents_review"];

export default function AdminFarmerApplications() {
  const [rows, setRows] = useState<FarmerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [officerFilter, setOfficerFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<FarmerApplication>("farmer_applications", "submitted_at"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load applications.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<FarmerApplication>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("farmer_applications", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (stageFilter !== "all" && row.stage !== stageFilter) return false;
      if (officerFilter !== "all" && (row.assigned_officer || "Unassigned") !== officerFilter) return false;
      if (!query) return true;
      return [row.farmer_name, row.farm_name || "", row.village || "", row.mobile || ""]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [rows, search, stageFilter, officerFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const columns: Column<FarmerApplication>[] = [
    {
      key: "farmer",
      header: "Applicant",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.farmer_name}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.farm_name || "—"}</p>
        </div>
      ),
    },
    {
      key: "village",
      header: "Village",
      render: (row) => (
        <span className="text-sm text-foreground-700 whitespace-nowrap">{row.village || "—"}</span>
      ),
    },
    {
      key: "size",
      header: "Farm size",
      render: (row) => <span className="text-sm text-foreground-700">{row.farm_size || "—"}</span>,
    },
    {
      key: "stage",
      header: "Stage",
      render: (row) => <StatusPill {...metaFor(APPLICATION_STAGE, row.stage)} />,
    },
    {
      key: "score",
      header: "Score",
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-16 h-1.5 rounded-full bg-background-200 overflow-hidden">
            <div className="h-full rounded-full bg-primary-500" style={{ width: `${row.score}%` }}></div>
          </div>
          <span className="text-sm text-foreground-700">{row.score}</span>
        </div>
      ),
    },
    {
      key: "submitted",
      header: "Submitted",
      render: (row) => (
        <span className="text-xs text-foreground-500 whitespace-nowrap">
          {formatDate(row.submitted_at)}
        </span>
      ),
    },
    {
      key: "officer",
      header: "Officer",
      render: (row) => (
        <span className="text-sm text-foreground-700 whitespace-nowrap">
          {row.assigned_officer || "Unassigned"}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Farm Network"
        title="Farmer Applications"
        subtitle="Move each applicant through screening, field verification, documents, training and pilot before approval."
      />

      <StatCards
        stats={[
          { label: "Total applications", value: rows.length, icon: "ri-file-user-line", tone: "primary" },
          {
            label: "New",
            value: rows.filter((row) => row.stage === "submitted").length,
            icon: "ri-inbox-line",
            tone: "accent",
          },
          {
            label: "In review",
            value: rows.filter((row) => IN_REVIEW.includes(row.stage)).length,
            icon: "ri-search-eye-line",
            tone: "secondary",
          },
          {
            label: "Approved",
            value: rows.filter((row) => row.stage === "approved").length,
            icon: "ri-checkbox-circle-line",
            tone: "primary",
          },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by name, farm, village…" />
          <Select
            label="Stage"
            value={stageFilter}
            onChange={setStageFilter}
            options={[{ value: "all", label: "All stages" }, ...optionsFrom(APPLICATION_STAGE)]}
          />
          <Select
            label="Officer"
            value={officerFilter}
            onChange={setOfficerFilter}
            options={[
              { value: "all", label: "All officers" },
              ...OFFICERS.map((officer) => ({ value: officer, label: officer })),
            ]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">
            {filtered.length} applications
          </span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading applications…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-file-user-line"
            title={rows.length === 0 ? "No applications yet" : "No applications match"}
            message={
              rows.length === 0
                ? "Farm-partner applications submitted from the website will land here."
                : "Try a different search or clear the filters."
            }
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.farmer_name || "Application"}
        subtitle="Farmer application"
        footer={
          selected ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => update(selected.id, { stage: "approved" }, "Application approved.")}
                className={btnPrimary}
              >
                <i className="ri-check-line"></i> Approve
              </button>
              <button
                type="button"
                onClick={() => update(selected.id, { stage: "rejected" }, "Application rejected.")}
                className={btnGhost}
              >
                <i className="ri-close-line"></i> Reject
              </button>
            </div>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(APPLICATION_STAGE, selected.stage)} />
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full">
                <i className="ri-bar-chart-line"></i> Score {selected.score}/100
              </span>
            </div>

            <DetailList
              items={[
                { icon: "ri-phone-line", label: "Mobile", value: selected.mobile || "—" },
                { icon: "ri-mail-line", label: "Email", value: selected.email || "—" },
                { icon: "ri-map-pin-line", label: "Village / District", value: `${selected.village} · ${selected.district}` },
                { icon: "ri-plant-line", label: "Farm name", value: selected.farm_name || "—" },
                { icon: "ri-ruler-line", label: "Farm size", value: selected.farm_size || "—" },
                { icon: "ri-seedling-line", label: "Farm type / crops", value: `${selected.farm_type || "—"} · ${selected.crops || "—"}` },
                { icon: "ri-home-gear-line", label: "Facilities", value: selected.facilities || "—" },
                { icon: "ri-lightbulb-line", label: "Experience ideas", value: selected.experience_ideas || "—" },
                { icon: "ri-bank-line", label: "Bank details", value: selected.bank_details || "—" },
                { icon: "ri-calendar-line", label: "Submitted", value: formatDate(selected.submitted_at) },
              ]}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Pipeline stage">
                <select
                  value={selected.stage}
                  onChange={(event) => update(selected.id, { stage: event.target.value })}
                  className={fieldInput}
                >
                  {optionsFrom(APPLICATION_STAGE).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Assigned field officer">
                <select
                  value={selected.assigned_officer || "Unassigned"}
                  onChange={(event) => update(selected.id, { assigned_officer: event.target.value })}
                  className={fieldInput}
                >
                  {OFFICERS.map((officer) => (
                    <option key={officer} value={officer}>
                      {officer}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Review score">
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={selected.score}
                  onChange={(event) => update(selected.id, { score: Number(event.target.value) })}
                  className="flex-1 cursor-pointer accent-primary-500"
                />
                <span className="text-sm font-semibold text-foreground-900 w-10 text-right">
                  {selected.score}
                </span>
              </div>
            </Field>

            <Field label="Internal notes">
              <textarea
                rows={3}
                value={selected.notes || ""}
                onChange={(event) => setRows((list) => list.map((row) => (row.id === selected.id ? { ...row, notes: event.target.value } : row)))}
                onBlur={() => update(selected.id, { notes: selected.notes })}
                placeholder="Field visit notes, document checks, follow-ups…"
                className="w-full bg-background-100 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              ></textarea>
            </Field>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => update(selected.id, { stage: "field_verification" }, "Field verification scheduled.")}
                className={btnAccent}
              >
                <i className="ri-calendar-check-line"></i> Schedule verification
              </button>
              {selected.mobile ? (
                <a href={`tel:${selected.mobile}`} className={btnGhost}>
                  <i className="ri-phone-line"></i> Call applicant
                </a>
              ) : null}
            </div>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>
      {node}
    </div>
  );
}